import React, { useState, useEffect } from 'react';
import { FaFileExcel, FaSearch } from 'react-icons/fa';
import UserTable from '../components/users/UserTable';
import BanModal from '../components/users/BanModal';
import UserAuditModal from '../components/users/UserAuditModal';
import { supabase, supabaseAdmin } from '../services/supabase';
import adminService from '../services/admin.service';
import { useAuth } from '../context/AuthContext';

const exportToCSV = (rows, filename) => {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(','), ...rows.map(r => keys.map(k => `"${String(r[k] ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

function UsersPage() {
  const { admin } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showBanModal, setShowBanModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [selectedUserForAudit, setSelectedUserForAudit] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [searchTerm, filterStatus, users]);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .or('user_type.is.null,user_type.neq.business')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Check ban status (error olsa da davam et)
      const usersWithBanStatus = await Promise.all(
        data.map(async (user) => {
          let banData = null;
          try {
            const { data: ban } = await supabase
              .from('user_bans')
              .select('*')
              .eq('user_id', user.id)
              .maybeSingle();

            banData = ban;
          } catch (err) {
            console.log('Ban check error:', err);
          }

          // Check-in count
          let checkinCount = 0;
          try {
            const { count } = await supabase
              .from('check_ins')
              .select('*', { count: 'exact', head: true })
              .eq('user_id', user.id);
            checkinCount = count || 0;
          } catch (err) {
            console.log('Check-in count error:', err);
          }

          return {
            ...user,
            is_banned: !!banData,
            ban_info: banData,
            total_checkins: checkinCount,
          };
        })
      );

      setUsers(usersWithBanStatus);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Xəta baş verdi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Search
    if (searchTerm) {
      filtered = filtered.filter(
        (u) =>
          u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter((u) => {
        if (filterStatus === 'active') return !u.is_banned;
        if (filterStatus === 'banned') return u.is_banned;
        return true;
      });
    }

    setFilteredUsers(filtered);
  };

  const handleBan = (user) => {
    setSelectedUser(user);
    setShowBanModal(true);
  };

  const handleBanConfirm = async (userId, reason, duration) => {
    try {
      // Ban date hesabla
      let bannedUntil = null;
      if (duration === '7days') {
        bannedUntil = new Date();
        bannedUntil.setDate(bannedUntil.getDate() + 7);
      } else if (duration === '30days') {
        bannedUntil = new Date();
        bannedUntil.setDate(bannedUntil.getDate() + 30);
      }

      // Ban et
      const { error } = await supabase.from('user_bans').insert({
        user_id: userId,
        reason,
        banned_until: bannedUntil?.toISOString(),
        banned_by: admin.id,
      });

      if (error) throw error;

      // Log yaz
      await adminService.logAction(admin.id, 'ban_user', 'user', userId, {
        reason,
        duration,
      });

      alert('User uğurla ban edildi!');
      setShowBanModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error banning user:', error);
      alert('Xəta baş verdi!');
    }
  };

  const handleUnban = async (user) => {
    if (!window.confirm(`${user.full_name} ban-ı ləğv etmək istəyirsiniz?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('user_bans')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;

      // Log yaz
      await adminService.logAction(admin.id, 'unban_user', 'user', user.id);

      alert('Ban uğurla ləğv edildi!');
      fetchUsers();
    } catch (error) {
      console.error('Error unbanning user:', error);
      alert('Xəta baş verdi!');
    }
  };

  const handleEditPoints = async (user) => {
    const newPoints = prompt(`${user.full_name} üçün yeni xal miqdarı:`, user.points || 0);

    if (newPoints === null) return;

    try {
      const { error } = await supabase
        .from('users')
        .update({ points: parseInt(newPoints) })
        .eq('id', user.id);

      if (error) throw error;

      // Log yaz
      await adminService.logAction(admin.id, 'edit_points', 'user', user.id, {
        old_points: user.points,
        new_points: parseInt(newPoints),
      });

      alert('Xallar uğurla dəyişdirildi!');
      fetchUsers();
    } catch (error) {
      console.error('Error editing points:', error);
      alert('Xəta baş verdi!');
    }
  };

  const handleView = (user) => {
    setSelectedUserForAudit(user);
    setShowAuditModal(true);
  };

  const handleDelete = async (user) => {
    if (
      !window.confirm(
        `XƏBƏRDARLIQ: ${user.full_name} silinəcək! Bütün data silinəcək. Əminsiniz?`
      )
    ) {
      return;
    }

    if (!window.confirm('Son təsdiq! Geriyə dönüş yoxdur!')) {
      return;
    }

    try {
      const uid = user.id;

      // 1. Əlaqəli bütün records-u sil (foreign key violation-dan qaçmaq üçün)
      const tables = [
        { table: 'notifications',       field: 'user_id' },
        { table: 'user_bans',           field: 'user_id' },
        { table: 'meetup_participants', field: 'user_id' },
        { table: 'chat_messages',       field: 'user_id' },
        { table: 'check_ins',           field: 'user_id' },
        { table: 'reviews',             field: 'user_id' },
        { table: 'point_transactions',  field: 'user_id' },
        { table: 'point_logs',          field: 'user_id' },
        { table: 'user_badges',         field: 'user_id' },
        { table: 'user_follows',        field: 'follower_id' },
        { table: 'user_follows',        field: 'following_id' },
        { table: 'business_follows',    field: 'user_id' },
        { table: 'reservations',        field: 'user_id' },
        { table: 'daily_spins',         field: 'user_id' },
      ];

      for (const { table, field } of tables) {
        try {
          await supabase.from(table).delete().eq(field, uid);
        } catch (_) {}
      }

      // Friendships (iki tərəfdən)
      try {
        await supabase.from('friendships').delete().eq('user_id', uid);
        await supabase.from('friendships').delete().eq('friend_id', uid);
      } catch (_) {}

      // Meetups (creator silindikdə)
      try {
        // Əvvəl meetup-lara aid chat_messages-i sil
        const { data: myMeetups } = await supabase
          .from('meetups').select('id').eq('creator_id', uid);
        if (myMeetups?.length) {
          const meetupIds = myMeetups.map(m => m.id);
          await supabase.from('chat_messages').delete().in('meetup_id', meetupIds);
          await supabase.from('meetup_participants').delete().in('meetup_id', meetupIds);
          await supabase.from('meetups').delete().eq('creator_id', uid);
        }
      } catch (_) {}

      // 2. public.users sil (supabaseAdmin RLS bypass edir)
      const client = supabaseAdmin || supabase;
      const { error: userError } = await client.from('users').delete().eq('id', uid);
      if (userError) throw userError;

      // 3. auth.users-dan da sil (supabaseAdmin varsa)
      if (supabaseAdmin) {
        try {
          await supabaseAdmin.auth.admin.deleteUser(uid);
        } catch (_) {
          // auth.users-da olmaya bilər — ignore
        }
      }

      alert('User uğurla silindi!');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Xəta baş verdi: ' + error.message);
    }
  };

  // Checkbox helpers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredUsers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredUsers.map(u => u.id)));
    }
  };

  const handleBulkBan = async () => {
    if (!selectedIds.size) return;
    const reason = prompt('Ban səbəbi:');
    if (!reason) return;
    const items = filteredUsers.filter(u => selectedIds.has(u.id) && !u.is_banned);
    for (const user of items) {
      try {
        await supabase.from('user_bans').insert({
          user_id: user.id,
          reason,
          banned_until: null,
          banned_by: admin.id,
        });
        await adminService.logAction(admin.id, 'ban_user', 'user', user.id, { reason, duration: 'permanent' });
      } catch (e) { console.error('Bulk ban error:', e); }
    }
    setSelectedIds(new Set());
    fetchUsers();
    alert(`${items.length} user ban edildi!`);
  };

  const handleBulkUnban = async () => {
    if (!selectedIds.size) return;
    const items = filteredUsers.filter(u => selectedIds.has(u.id) && u.is_banned);
    for (const user of items) {
      try {
        await supabase.from('user_bans').delete().eq('user_id', user.id);
        await adminService.logAction(admin.id, 'unban_user', 'user', user.id);
      } catch (e) { console.error('Bulk unban error:', e); }
    }
    setSelectedIds(new Set());
    fetchUsers();
    alert(`${items.length} user-in banı ləğv edildi!`);
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>Yüklənir...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>👥 İstifadəçi İdarəetməsi</h1>
          <p style={styles.subtitle}>Bütün istifadəçiləri idarə edin</p>
        </div>
        <button style={styles.btnExport} onClick={() => exportToCSV(filteredUsers.map(u => ({
          id: u.id, full_name: u.full_name, username: u.username, email: u.email,
          points: u.points, total_checkins: u.total_checkins, is_banned: u.is_banned,
          created_at: u.created_at,
        })), 'users.csv')}>
          <FaFileExcel /> Export Excel
        </button>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div style={styles.bulkBar}>
          <span style={styles.bulkInfo}>{selectedIds.size} seçildi</span>
          <button onClick={handleBulkBan} style={styles.bulkBan}>🚫 Hamısını Ban Et</button>
          <button onClick={handleBulkUnban} style={styles.bulkUnban}>✅ Hamısının Banını Ləğv Et</button>
          <button onClick={() => setSelectedIds(new Set())} style={styles.bulkClear}>Ləğv Et</button>
        </div>
      )}

      {/* Filters */}
      <div style={styles.filters}>
        <div style={styles.searchBox}>
          <FaSearch style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Ad, username və ya email ilə axtarış..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="all">Hamısı</option>
          <option value="active">Aktiv</option>
          <option value="banned">Ban edilmiş</option>
        </select>
      </div>

      {/* Stats */}
      <div style={styles.stats}>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Ümumi:</span>
          <span style={styles.statValue}>{users.length}</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Aktiv:</span>
          <span style={{ ...styles.statValue, color: '#4CAF50' }}>
            {users.filter((u) => !u.is_banned).length}
          </span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Ban:</span>
          <span style={{ ...styles.statValue, color: '#F44336' }}>
            {users.filter((u) => u.is_banned).length}
          </span>
        </div>
      </div>

      {/* Table */}
      <UserTable
        users={filteredUsers}
        onView={handleView}
        onBan={handleBan}
        onUnban={handleUnban}
        onEditPoints={handleEditPoints}
        onDelete={handleDelete}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
      />

      {/* Ban Modal */}
      {showBanModal && selectedUser && (
        <BanModal
          user={selectedUser}
          onConfirm={handleBanConfirm}
          onClose={() => {
            setShowBanModal(false);
            setSelectedUser(null);
          }}
        />
      )}

      {/* Audit Modal */}
      {showAuditModal && selectedUserForAudit && (
        <UserAuditModal
          user={selectedUserForAudit}
          onClose={() => {
            setShowAuditModal(false);
            setSelectedUserForAudit(null);
          }}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1800px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '20px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '5px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#999',
  },
  btnExport: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  filters: {
    display: 'flex',
    gap: '15px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  searchBox: {
    flex: 1,
    minWidth: '300px',
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    left: '15px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#999',
  },
  searchInput: {
    width: '100%',
    padding: '12px 15px 12px 45px',
    fontSize: '14px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
  },
  filterSelect: {
    padding: '12px 15px',
    fontSize: '14px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
    cursor: 'pointer',
    backgroundColor: '#fff',
  },
  stats: {
    display: 'flex',
    gap: '15px',
    marginBottom: '20px',
  },
  statCard: {
    flex: 1,
    padding: '15px 20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statLabel: {
    fontSize: '14px',
    color: '#666',
    fontWeight: '600',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1A1A2E',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #FF6B35',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 20px',
    backgroundColor: '#E3F2FD',
    borderRadius: '8px',
    marginBottom: '15px',
    flexWrap: 'wrap',
  },
  bulkInfo: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1A1A2E',
    flex: 1,
  },
  bulkBan: {
    padding: '8px 16px',
    backgroundColor: '#F44336',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  bulkUnban: {
    padding: '8px 16px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  bulkClear: {
    padding: '8px 16px',
    backgroundColor: '#9E9E9E',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default UsersPage;